/**
 * Dev-only bridge: open a session from a custom token.
 *
 * The tenant portal (`/tenant/**`) is only reachable with a real tenant
 * session, and the only manual way to get one ends in "create an account with a
 * password" — which no automated agent may do. `scripts/dev-tenant-account.js`
 * provisions a PASSWORDLESS account and mints a short-lived custom token; this
 * is the other half, the thing that consumes it.
 *
 * Never *installed* for users. Two independent gates must both pass before
 * either function is attached to `window`:
 *
 *   1. `environment.production` is false — in a production build
 *      `provideDevAuthBridge()` returns an empty provider array, so no
 *      APP_INITIALIZER is registered and nothing here ever runs;
 *   2. the page is served from `localhost` / `127.0.0.1` — a deployed dev
 *      origin gets nothing either, so a leaked token cannot be replayed there.
 *
 * Belt and braces on purpose: gate 1 alone would already be enough, but it
 * depends on the right environment file being swapped in at build time, and
 * that is a build-configuration mistake away from being wrong.
 *
 * What these gates do NOT do is remove the code. Measured on a real
 * `ng build --configuration production`: both global names and the assignment
 * that installs them are still in `main.*.js`. `environment.production` is read
 * at runtime, not folded at build time, so the bundler keeps the branch. That
 * is dead code, not a live door — but do not claim it is tree-shaken, because
 * it is not.
 */

import { APP_INITIALIZER, Provider } from '@angular/core';
import { Auth, signInWithCustomToken, signInWithEmailLink } from '@angular/fire/auth';
import { environment } from 'environments/environment';

/** Custom-token entry point. Needs IAM signBlob on the project. */
export const DEV_LOGIN_GLOBAL = '__proporioDevLogin';

/**
 * Sign-in-link entry point. Preferred: it needs no IAM grant, only the
 * "Email link" provider enabled — see scripts/dev-tenant-account.js.
 */
export const DEV_LOGIN_LINK_GLOBAL = '__proporioDevLoginLink';

/** Hostnames allowed to expose the bridge. */
const LOCAL_HOSTS = ['localhost', '127.0.0.1'];

export interface DevAuthBridgeHost {
  readonly location?: { readonly hostname?: string };
  [key: string]: unknown;
}

/**
 * Whether this context may expose the bridge. Exported so the decision is
 * testable without booting the app or touching a real `window`.
 */
export function isDevBridgeAllowed(
  host: DevAuthBridgeHost | undefined,
  isProduction: boolean
): boolean {
  if (isProduction) return false;
  const hostname = host?.location?.hostname;
  return !!hostname && LOCAL_HOSTS.includes(hostname);
}

/**
 * Attach both entry points when allowed. Returns true when the bridge was
 * installed, false when the context was refused — the caller logs nothing on
 * refusal, since silence is the expected state everywhere but a local dev
 * build.
 */
export function installDevAuthBridge(
  host: DevAuthBridgeHost | undefined,
  auth: Auth,
  isProduction: boolean
): boolean {
  if (!host || !isDevBridgeAllowed(host, isProduction)) return false;

  host[DEV_LOGIN_GLOBAL] = (token: string) => signInWithCustomToken(auth, token);
  host[DEV_LOGIN_LINK_GLOBAL] = (email: string, link: string) =>
    signInWithEmailLink(auth, email, link);
  return true;
}

/**
 * Provider for `app.config.ts`. Empty in production, so the initializer is
 * never registered and `installDevAuthBridge` is never reached. The code still
 * ships — see the note at the top of this file.
 */
export function provideDevAuthBridge(): Provider[] {
  if (environment.production) return [];

  return [
    {
      provide: APP_INITIALIZER,
      multi: true,
      deps: [Auth],
      useFactory: (auth: Auth) => () => {
        const installed = installDevAuthBridge(
          typeof window === 'undefined'
            ? undefined
            : (window as unknown as DevAuthBridgeHost),
          auth,
          environment.production
        );
        if (installed) {
          console.info(
            `[dev] ${DEV_LOGIN_LINK_GLOBAL}(email, link) and ${DEV_LOGIN_GLOBAL}(token) ` +
              `are available — see scripts/dev-tenant-account.js`
          );
        }
      }
    }
  ];
}
